import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Plus, Loader2, X, Power, Pencil, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";

const ROLE_OPTS = [
  { v: "admin", l: "Administrateur" },
  { v: "planification", l: "Planification" },
  { v: "technicien", l: "Technicien" },
  { v: "installateur", l: "Partenaire" },
];
const EMPTY = { prenom: "", nom: "", email: "", telephone: "", role: "technicien", entreprise_id: "", actif: true, password: "", adresse: "", code_postal: "", ville: "" };

export default function Utilisateurs() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [rows, setRows] = useState(null);
  const [ents, setEnts] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [prepay, setPrepay] = useState("aucun");
  const [villeOpts, setVilleOpts] = useState([]);

  const load = async () => {
    const p = new URLSearchParams();
    if (search) p.set("search", search);
    if (roleFilter) p.set("role", roleFilter);
    const { data } = await api.get(`/users?${p}`);
    setRows(data);
  };
  useEffect(() => { load(); }, [search, roleFilter]);
  useEffect(() => { api.get("/entreprises").then((r) => setEnts(r.data)).catch(() => {}); }, []);

  // Suggestion de villes à partir du code postal (API officielle geo.api.gouv.fr)
  useEffect(() => {
    const cp = form.code_postal;
    if (!showForm || !cp || cp.length !== 5) { setVilleOpts([]); return; }
    let active = true;
    fetch(`https://geo.api.gouv.fr/communes?codePostal=${cp}&fields=nom&format=json`)
      .then((r) => r.json())
      .then((d) => {
        if (!active) return;
        const names = Array.isArray(d) ? d.map((x) => x.nom) : [];
        setVilleOpts(names);
        // Cohérence CP/Ville : si la ville actuelle n'appartient pas aux communes du CP, on prend la 1ère.
        if (names.length && !names.includes(form.ville)) setForm((f) => ({ ...f, ville: names[0] }));
      })
      .catch(() => active && setVilleOpts([]));
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.code_postal, showForm]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setPrepay("aucun"); setShowForm(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ ...EMPTY, ...u, password: "", entreprise_id: u.entreprise_id || "" });
    const ent = ents.find((e) => e.id === u.entreprise_id);
    setPrepay(ent?.prepayment_mode || "aucun");
    setShowForm(true);
  };

  const onEntreprise = (eid) => {
    setForm((f) => ({ ...f, entreprise_id: eid }));
    const ent = ents.find((e) => e.id === eid);
    setPrepay(ent?.prepayment_mode || "aucun");
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, entreprise_id: form.entreprise_id || null };
    try {
      if (editing) await api.put(`/users/${editing.id}`, payload);
      else await api.post("/users", payload);
      if (form.role === "installateur" && form.entreprise_id) {
        await api.patch(`/entreprises/${form.entreprise_id}/prepayment`, { prepayment_mode: prepay });
      }
      toast.success(editing ? "Utilisateur modifié" : "Utilisateur créé");
      setShowForm(false);
      load();
      api.get("/entreprises").then((r) => setEnts(r.data)).catch(() => {});
    } catch (err) { toast.error(apiError(err)); }
  };

  const toggle = async (u) => { try { await api.patch(`/users/${u.id}/toggle`); load(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (u) => { if (!window.confirm(`Supprimer ${u.prenom} ${u.nom} ?`)) return; try { await api.delete(`/users/${u.id}`); toast.success("Supprimé"); load(); } catch (e) { toast.error(apiError(e)); } };
  const resetPwd = async (u) => { if (!window.confirm(`Envoyer un lien de réinitialisation du mot de passe à ${u.prenom} ${u.nom} (${u.email}) ?`)) return; try { const { data } = await api.post(`/users/${u.id}/reset-password`, { origin_url: window.location.origin }); toast.success(data.email_sent ? `Lien de réinitialisation envoyé à ${data.email}` : "Lien généré (e-mail non configuré sur cet environnement)"); } catch (e) { toast.error(apiError(e)); } };

  if (!rows) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">Utilisateurs</h1>
          <p className="text-sm text-slate-500">{rows.length} utilisateur{rows.length > 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="user-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" className="w-44 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400" />
          <select data-testid="user-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
            <option value="">Tous rôles</option>
            {ROLE_OPTS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
          {isAdmin && <button data-testid="user-add" onClick={openNew} className="flex items-center gap-2 rounded-lg bg-[#0A0A0A] px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Ajouter</button>}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] text-left text-sm" data-testid="users-table">
            <thead><tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2.5">Nom</th><th className="px-4 py-2.5">Email</th><th className="px-4 py-2.5">Téléphone</th><th className="px-4 py-2.5">Rôle</th><th className="px-4 py-2.5">Entreprise</th><th className="px-4 py-2.5">Statut</th>{isAdmin && <th className="px-4 py-2.5 text-right">Actions</th>}
            </tr></thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-2.5 font-medium text-slate-900">{u.prenom} {u.nom}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{u.email}</td>
                  <td className="px-4 py-2.5"><span className="mono text-xs text-slate-600">{u.telephone}</span></td>
                  <td className="px-4 py-2.5"><span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">{u.role_label}</span></td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{u.entreprise_nom || "—"}</td>
                  <td className="px-4 py-2.5">{u.actif ? <span className="text-xs font-semibold text-emerald-600">Actif</span> : <span className="text-xs font-semibold text-slate-400">Inactif</span>}</td>
                  {isAdmin && (
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-1">
                        <button data-testid={`user-edit-${u.id}`} onClick={() => openEdit(u)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                        {isAdmin && <button data-testid={`user-reset-${u.id}`} onClick={() => resetPwd(u)} title="Réinitialiser le mot de passe" className="rounded p-1.5 text-slate-500 hover:bg-slate-100"><KeyRound className="h-4 w-4" /></button>}
                        <button data-testid={`user-toggle-${u.id}`} onClick={() => toggle(u)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100"><Power className="h-4 w-4" /></button>
                        <button data-testid={`user-delete-${u.id}`} onClick={() => remove(u)} className="rounded p-1.5 text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowForm(false)} />
          <form onSubmit={save} className="relative z-10 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl" data-testid="user-form">
            <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-lg font-bold">{editing ? "Modifier" : "Nouvel"} utilisateur</h2><button type="button" onClick={() => setShowForm(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <div className="grid grid-cols-2 gap-3">
              <FF label="Prénom"><input required className="fi" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} /></FF>
              <FF label="Nom"><input required className="fi" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} /></FF>
              <FF label="Email"><input required type="email" className="fi" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></FF>
              <FF label="Téléphone"><input className="fi" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} /></FF>
              <FF label="Rôle">
                <select data-testid="user-role" className="fi" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  {ROLE_OPTS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                </select>
              </FF>
              <FF label="Entreprise">
                <select className="fi" value={form.entreprise_id} onChange={(e) => onEntreprise(e.target.value)}>
                  <option value="">Aucune</option>
                  {ents.map((en) => <option key={en.id} value={en.id}>{en.raison_sociale}</option>)}
                </select>
              </FF>
              <FF label={editing ? "Nouveau mot de passe (optionnel)" : "Mot de passe (défaut cvt2026)"}><input type="password" className="fi" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></FF>
              <FF label="Actif"><select className="fi" value={form.actif ? "1" : "0"} onChange={(e) => setForm({ ...form, actif: e.target.value === "1" })}><option value="1">Actif</option><option value="0">Inactif</option></select></FF>
            </div>
            {form.role === "installateur" && (
              <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50 p-3">
                <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-violet-700">Mode de facturation du partenaire</div>
                <select data-testid="user-prepay" className="fi" value={prepay} onChange={(e) => setPrepay(e.target.value)} disabled={!form.entreprise_id}>
                  <option value="aucun">Hors-prépaiement (facturation classique)</option>
                  <option value="prepaiement">Prépaiement (jetons)</option>
                </select>
                {!form.entreprise_id && <p className="mt-2 text-[11px] text-rose-500">Sélectionnez d'abord une entreprise pour définir le mode.</p>}
                <p className="mt-2 text-[11px] text-slate-500">En prépaiement, le partenaire achète des jetons par type de VT et en consomme un à chaque rendez-vous créé.</p>
              </div>
            )}
            {form.role === "technicien" && (
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">Domicile (départ / retour de tournée)</div>
                <div className="grid grid-cols-2 gap-3">
                  <FF label="Adresse"><input data-testid="user-adresse" className="fi" value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} /></FF>
                  <FF label="Code postal"><input data-testid="user-cp" className="fi" value={form.code_postal} onChange={(e) => setForm({ ...form, code_postal: e.target.value.replace(/[^0-9]/g, "").slice(0, 5) })} placeholder="Ex : 69008" /></FF>
                  <FF label="Ville"><input data-testid="user-ville" className="fi" list="user-villes" value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} placeholder="Sélectionner ou saisir" /><datalist id="user-villes">{villeOpts.map((o) => <option key={o} value={o} />)}</datalist></FF>
                </div>
                <p className="mt-2 text-[11px] text-slate-400">Le code postal est géolocalisé pour servir de point de départ et de retour aux tournées.</p>
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold hover:bg-slate-50">Annuler</button>
              <button data-testid="user-form-submit" className="rounded-lg bg-[#0A0A0A] px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">{editing ? "Enregistrer" : "Créer"}</button>
            </div>
          </form>
          <style>{`.fi{width:100%;border:1px solid #e2e8f0;border-radius:.5rem;padding:.5rem .625rem;font-size:.875rem;outline:none}.fi:focus{border-color:#94a3b8}`}</style>
        </div>
      )}
    </div>
  );
}
function FF({ label, children }) {
  return <div><label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</label>{children}</div>;
}
